import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close } from "@mui/icons-material";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { UserTypeId, validateEmail } from "../../common/const";
import { getMemberList, inviteMember } from "../../api/memberListApi";


const ProjectMemberList = ({
    fromMentor,
    projectId,
    studentData
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [sendInvite, setSendInvite] = useState(false);
    const [errors, setErrors] = useState({});
    const [teamList, setTeamList] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Name is required';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await inviteMember({
                project_id: projectId,
                user_name: formData?.name,
                user_email: formData?.email,
                role: 'STUDENT'
            });
            if (response?.success) {
                showNotification(response?.message, 'success');
                setFormData({
                    name: '',
                    email: ''
                });
                setSendInvite(false);
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getMembers = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getMemberList(projectId);
            dispatch(setLoading({
                loading: false
            }));
            setTeamList(response?.data);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (projectId) {
            getMembers();
        }
    }, [projectId]);

    useEffect(() => {
        if (studentData?.user_id && !teamList.find((a) => a.isOwner === true)) {
            teamList.unshift({
                name: `${studentData?.first_name} ${studentData?.last_name}`,
                usertype: 'Student',
                isOwner: true
            });
        }
    }, [studentData, projectId]);

    return (
        <Fragment>
            <CommonDialog
                open={sendInvite}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Invite Team Member</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setSendInvite(false);
                                    setFormData({
                                        name: '',
                                        email: '',
                                    });
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={0.5}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter name and email address for invite member</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Name"
                                type="email"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name}
                                placeholder="Enter Name"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                error={errors.email}
                                placeholder="Enter email"
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Invite"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={2}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>Team & Members</Typography>
                                <Typography fontSize={14} color={theme.palette.background.dark}>Manage project collaborators and roles.</Typography>
                            </Box>
                            {!fromMentor && user?.user_type_id === UserTypeId.Student && (
                                <CommonButton
                                    label="Invite"
                                    height="35px"
                                    width="80px"
                                    startIcon={<Add />}
                                    marginBottom="0px"
                                    marginTop="0px"
                                    handleClick={() => setSendInvite(true)}
                                />
                            )}
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        sx={{
                            height: '15vh',
                            overflow: 'auto'
                        }}
                    >
                        {teamList.map((a, index) => {
                            return (
                                <Fragment key={index}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        padding="0px 10px 10px 10px"
                                        alignItems="center"
                                    >
                                        <Box display="flex" gap={1} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold',
                                                    backgroundColor: index % 2 === 0 ? '#0088FF' : '#34C759',
                                                    color: 'white'
                                                }}
                                            >
                                                {(a?.name || a?.user_name)?.split(' ')?.map(n => n[0])?.join('')}
                                            </Box>
                                            <Box>
                                                <Typography fontSize={15} fontWeight="600">{a?.name || a?.user_name}</Typography>
                                                <Typography
                                                    fontSize={14}
                                                    color={theme.palette.background.dark}
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {a?.usertype || 'Collaborator'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {a.isOwner && (
                                            <Box
                                                sx={{
                                                    background: '#01875F',
                                                    padding: '3px 8px',
                                                    textAlign: 'center',
                                                    marginBottom: 0.5,
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography fontSize={13} color={'white'}>Owner</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Fragment>
                            )
                        })}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default ProjectMemberList;